/**
 * StudyReaderPage — テキスト閲覧 (FR-03/FR-04/FR-18)
 * 閲覧で「閲覧済み」を自動記録。出典ブロック常設。
 */

import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { getStudyText, markStudyTextViewed } from '../api'
import type { StudyTextDetail } from '../api/types'
import SourceBlock from '../components/SourceBlock'
import Icon from '../components/Icon'
import { PageLoader } from '../components/Spinner'

export default function StudyReaderPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [text, setText] = useState<StudyTextDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!id) return
    setLoading(true)
    getStudyText(id)
      .then((t) => {
        setText(t)
        // 閲覧済みを自動記録 (FR-04)
        if (t && !t.is_viewed) {
          markStudyTextViewed(id, { viewed_at: new Date().toISOString() }).catch(() => {
            // サイレント失敗（閲覧済み記録は非クリティカル）
          })
        }
      })
      .catch((err) => {
        setError(err.message ?? 'テキストの読み込みに失敗しました')
      })
      .finally(() => setLoading(false))
  }, [id])

  if (loading) return <PageLoader />

  const backButton = (
    <button
      onClick={() => navigate(-1)}
      className="mb-4 flex items-center gap-1.5 text-sm font-semibold text-accent transition-colors hover:text-accent-hover"
    >
      <Icon name="arrow-left" size={16} /> 戻る
    </button>
  )

  if (error || !text) {
    return (
      <div className="mx-auto max-w-content p-screen">
        {backButton}
        <div role="alert" className="alert-error">
          {error ?? 'テキストが見つかりません'}
        </div>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-content p-screen" data-testid="text-detail">
      {backButton}

      <div className="mb-1 text-xs text-text-muted">{text.category_name_ja}</div>
      <h1 className="mb-5 text-[22px] font-bold leading-tight tracking-tight text-text-primary">{text.title}</h1>

      {/* 本文 (FR-03) */}
      <div data-testid="text-body" className="mb-6 whitespace-pre-wrap text-base leading-[1.8] text-text-primary">
        {text.body}
      </div>

      {/* 出典ブロック (FR-18, 一級市民) */}
      <SourceBlock
        sourceLaw={text.source_law}
        sourceReference={text.source_reference}
        confirmedDate={text.confirmed_date}
        lawRevisionDate={text.law_revision_date}
      />

      {/* このテキストの問題を解くCTA */}
      <button
        data-testid="text-to-practice"
        onClick={() => navigate(`/practice?category_id=${text.category_id}`)}
        className="btn-primary mt-6 w-full"
      >
        このテキストの問題を解く
      </button>
    </div>
  )
}
